import React, { useEffect, useMemo } from "react";
import {
  Avatar,
  Badge,
  DropdownList,
  DropdownListDivider,
  DropdownListItem,
  Grid,
  GridColumn,
  Navbar,
  Typography,
} from "@sumprio/ui";
import { EyeIcon, LogoutIcon } from "@sumprio/ui/icons";
import "./Header.scss";

const brandActionIconColor = "var(--color-icon-brand-secondary, #2563eb)";
const logoutIconColor = "var(--color-text-danger, #c62828)";

const NAVBAR_AVATAR_TRIGGER_SELECTOR =
  "nav.navbar .navbar__avatar-dropdown button.dropdown__trigger--avatar";

function BrandEyeIcon(props: React.ComponentProps<typeof EyeIcon>) {
  return <EyeIcon {...props} color={brandActionIconColor} />;
}

function RedLogoutIcon(props: React.ComponentProps<typeof LogoutIcon>) {
  return <LogoutIcon {...props} color={logoutIconColor} />;
}

function initialsFromUserName(name: string | undefined): string {
  if (!name?.trim()) return "—";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export interface HeaderProps {
  companyName: string;
  avatarSrc?: string;
  avatarAlt?: string;
  userName?: string;
  avatarInitials?: string;
  onProfileClick: () => void;
  onLogout: () => void;
  onHelpClick?: () => void;
  helpDropdownContent?: React.ReactNode;
  clientNavbarDropdown?: boolean;
  linkedBusinessName?: string;
  linkedBusinessBadge?: string;
}

export default function Header({
  companyName,
  avatarSrc,
  avatarAlt = "User avatar",
  userName,
  avatarInitials,
  onProfileClick,
  onLogout,
  onHelpClick,
  helpDropdownContent,
  clientNavbarDropdown,
  linkedBusinessName,
  linkedBusinessBadge,
}: HeaderProps) {
  useEffect(() => {
    const label = userName?.trim();
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30;
    const apply = () => {
      if (cancelled) return;
      const btn = document.querySelector(NAVBAR_AVATAR_TRIGGER_SELECTOR);
      if (btn instanceof HTMLElement) {
        if (label) {
          btn.removeAttribute("title");
          btn.setAttribute("data-user-tooltip", label);
          btn.setAttribute("aria-label", `${label}, open account menu`);
        } else {
          btn.removeAttribute("title");
          btn.removeAttribute("data-user-tooltip");
          btn.setAttribute("aria-label", "Open account menu");
        }
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) requestAnimationFrame(apply);
    };
    requestAnimationFrame(apply);
    return () => {
      cancelled = true;
      const btn = document.querySelector(NAVBAR_AVATAR_TRIGGER_SELECTOR);
      if (btn instanceof HTMLElement) {
        btn.removeAttribute("title");
        btn.removeAttribute("data-user-tooltip");
      }
    };
  }, [userName, avatarSrc, avatarInitials, clientNavbarDropdown]);

  const avatar = useMemo(() => {
    if (avatarSrc) {
      return { src: avatarSrc, alt: avatarAlt };
    }
    const initials =
      avatarInitials?.trim() || (userName?.trim() ? initialsFromUserName(userName) : "") || "";
    if (initials && initials !== "—") {
      return { initials };
    }
    return undefined;
  }, [avatarSrc, avatarAlt, userName, avatarInitials]);

  const summaryInitials = (avatarInitials?.trim() || initialsFromUserName(userName)).slice(0, 3);
  const firmLabel = linkedBusinessName?.trim() || "—";

  const defaultDropdownContent = useMemo(
    () => (
      <DropdownList paddingY="sm">
        <DropdownListItem label="My Profile" onClick={onProfileClick} />
        <DropdownListDivider />
        <DropdownListItem
          label="Logout"
          onClick={onLogout}
          className="header__dropdown-item-logout"
        />
      </DropdownList>
    ),
    [onProfileClick, onLogout]
  );

  const clientDropdownContent = useMemo(
    () => (
      <DropdownList paddingY="sm" listWidth={320}>
        <Grid
          display="flex"
          alignItems="center"
          gap="sm"
          marginBottom="xs"
          paddingX="md"
          paddingY="sm"
          justifyContent="space-between"
        >
          <GridColumn span={12} display="flex" alignItems="center" gap="sm">
            <Avatar type="initial" size="md" initials={summaryInitials} />
            <Typography variant="body" bodySize="sm" bodyWeight="medium">
              {firmLabel}
            </Typography>
          </GridColumn>
          <GridColumn span={12}>
            {linkedBusinessBadge ? (
              <Badge size="sm" type="pill-outline" color="brand" label={linkedBusinessBadge} />
            ) : null}
          </GridColumn>
        </Grid>
        <DropdownListDivider />
        <DropdownListItem
          type="icon"
          icon={BrandEyeIcon as typeof EyeIcon}
          label={<span className="header__dropdown-item-brand-label">View Profile</span>}
          onClick={onProfileClick}
        />
        <DropdownListItem
          type="icon"
          icon={RedLogoutIcon as typeof LogoutIcon}
          label={<span className="header__dropdown-item-logout-label">Logout</span>}
          onClick={onLogout}
          className="header__dropdown-item-logout"
        />
      </DropdownList>
    ),
    [firmLabel, linkedBusinessBadge, onLogout, onProfileClick, summaryInitials]
  );

  const avatarDropdownContent = clientNavbarDropdown
    ? clientDropdownContent
    : defaultDropdownContent;

  return (
    <Navbar
      companyName={companyName}
      showHelpButton
      avatar={avatar ?? undefined}
      onHelpClick={onHelpClick}
      helpDropdownContent={helpDropdownContent}
      helpDropdownPlacement="bottom-end"
      avatarDropdownContent={avatarDropdownContent}
      avatarDropdownPlacement="bottom-start"
      sticky
    />
  );
}
